import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch, getUserFacingErrorMessage } from '../services/apiClient';
import { getCachedUser } from '../services/authService';
import { isUsableCandidateEmail } from '../utils/candidateAnalysisUtils';
import '../JobsPage.css';
import '../CandidateListPage.css';
import './InterviewEvaluationModal.css';

/** Non-empty saved interview evaluation (object, JSON string, or plain string). */
function hasPersistedInterviewEvaluation(results) {
  if (results == null || results === '') return false;
  if (typeof results === 'string') {
    const t = results.trim();
    if (!t) return false;
    try {
      const parsed = JSON.parse(t);
      return hasPersistedInterviewEvaluation(parsed);
    } catch {
      return true;
    }
  }
  if (typeof results === 'object' && !Array.isArray(results)) {
    return Object.keys(results).length > 0;
  }
  return true;
}

/** Parse FastAPI/Flask JSON error bodies for display (detail may be string or validation array). */
function formatApiErrorBody(raw) {
  if (!raw || typeof raw !== 'object') return '';
  const d = raw.detail ?? raw.error ?? raw.message;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && typeof item.msg === 'string') return item.msg;
        return '';
      })
      .filter(Boolean)
      .join('; ');
  }
  return '';
}

function mergeCandidateEmailOutcome(evalObj, kind, extra = {}) {
  if (!evalObj || typeof evalObj !== 'object') return evalObj;
  const now = new Date().toISOString();
  const outcome = {
    kind,
    sent_at: now,
    ...extra,
  };
  if (extra.override_reason) {
    outcome.override_at = now;
  }
  return {
    ...evalObj,
    candidate_email_outcome: outcome,
  };
}

function isCandidateOutcomeEmailSent(evaluationObj) {
  const k = evaluationObj?.candidate_email_outcome?.kind;
  return k === 'rejection' || k === 'next_round' || k === 'offer' || k === 'hired';
}

/** True when sending offer / next-round conflicts with a negative AI recommendation. */
function evaluationNeedsOverrideForPositiveAction(evaluationObj) {
  if (!evaluationObj || typeof evaluationObj !== 'object') return false;
  const rec = String(evaluationObj.recommendation || '').toLowerCase();
  if (rec.includes('do not hire') || rec.includes('no hire')) return true;
  const score = evaluationObj.overall_score;
  if (score !== undefined && score !== null && Number(score) < 50) return true;
  return false;
}

function emailOutcomeLabel(outcome) {
  if (!outcome?.kind) return '';
  switch (outcome.kind) {
    case 'rejection':
      return 'Rejection email sent — candidate notified';
    case 'next_round':
      return 'Next-round email sent';
    case 'offer':
      return 'Offer email sent';
    case 'hired':
      return 'Hire confirmation sent';
    default:
      return 'Candidate email sent';
  }
}

const InterviewEvaluationModal = ({ 
  isOpen, 
  onClose, 
  questionnaire, 
  evaluationCompleted = false,
  evaluationResults = null,
  evaluationPayload = null,
  candidateName, 
  fitScore, 
  jobTitle, 
  resumeId,
  onGenerateQuestionnaire,
  onCancelQuestionnaireGeneration = null,
  isGeneratingQuestionnaire = false,
  onQuestionnaireReady = null,
  onEvaluationSaved = null,
  jobDescription = null,
  jobId = null,
  candidateEmail = null,
  /** Company display name from job / company (GET /api/jobs/:id → company_name). */
  jobCompanyName = null,
}) => {
  console.log("🎯 ENHANCED InterviewEvaluationModal rendering with props:");
  console.log("🎯 isOpen:", isOpen);
  console.log("🎯 questionnaire:", questionnaire);
  console.log("🎯 candidateName:", candidateName);
  console.log("🎯 fitScore:", fitScore);
  console.log("🎯 jobTitle:", jobTitle);
  
  // Debug questionnaire structure
  if (questionnaire) {
    console.log("🎯 Questionnaire sections:", Object.keys(questionnaire));
    console.log("🎯 Introduction questions:", questionnaire.introduction?.length || 0);
    console.log("🎯 Behavioral questions:", questionnaire.behavioral?.length || 0);
    console.log("🎯 Technical questions:", questionnaire.technical);
  }
  const [answers, setAnswers] = useState({});
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState({});
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [savedEvaluation, setSavedEvaluation] = useState(null);
  const [savedPayload, setSavedPayload] = useState(null);
  const [showSavedResults, setShowSavedResults] = useState(false);
  const [bypassSavedEvaluation, setBypassSavedEvaluation] = useState(false);
  const [isReevaluationMode, setIsReevaluationMode] = useState(false);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // Choice mode: show two buttons when no questionnaire exists
  const [showChoiceMode, setShowChoiceMode] = useState(false);
  const [isLoadingSavedResults, setIsLoadingSavedResults] = useState(false);
  const [hasCheckedSavedResults, setHasCheckedSavedResults] = useState(false);
  // Transcript-assisted evaluation (manual paste -> extract answers)
  const [useTranscript, setUseTranscript] = useState(false);
  const [transcriptText, setTranscriptText] = useState('');
  const [isExtractingAnswers, setIsExtractingAnswers] = useState(false);
  const [transcriptTemporaryId, setTranscriptTemporaryId] = useState(null);
  const [isTranscriptEvaluating, setIsTranscriptEvaluating] = useState(false);
  const [transcriptEvalMode, setTranscriptEvalMode] = useState('map_questionnaire'); // 'map_questionnaire' | 'extract_qa'
  const [extractedQAPairs, setExtractedQAPairs] = useState([]);
  const [transcriptMeta, setTranscriptMeta] = useState(null); // { detected_speakers, interviewer_speakers, candidate_speakers, confidence, mode, multi_candidate_suspected }
  const [candidateSpeakerOverride, setCandidateSpeakerOverride] = useState('');
  const [interviewerSpeakersOverride, setInterviewerSpeakersOverride] = useState([]);
  const [forceLLMExtraction, setForceLLMExtraction] = useState(false);
  const [showCasualPreview, setShowCasualPreview] = useState(false);
  // Removed "Responses (read-only)" tab — questionnaire is already shown below when needed.
  // Force rebuild to generate new JS file

  // Flatten questionnaire into a single array for easier navigation
  const [flattenedQuestions, setFlattenedQuestions] = useState([]);
  const prevIsOpenRef = useRef(false);
  /** True if we opened on the questionnaire because eval props were stale; clear when props catch up or modal closes. */
  const staleQuestionnaireLatchRef = useRef(false);
  const transcriptTextareaRef = useRef(null);
  const [transcriptHighlight, setTranscriptHighlight] = useState(false);

  /** null | 'rejection' | 'next_round' | 'offer' — candidate email composer from evaluation results */
  const [emailModalKind, setEmailModalKind] = useState(null);
  const [rejectSubject, setRejectSubject] = useState('Update on your application');
  const [rejectCustomMessage, setRejectCustomMessage] = useState('');
  const [rejectCompanyName, setRejectCompanyName] = useState('');
  const [rejectSending, setRejectSending] = useState(false);
  const [rejectResult, setRejectResult] = useState(null);
  /** Shown under evaluation results after a successful candidate email send */
  const [outcomeFeedback, setOutcomeFeedback] = useState(null);
  const [decisionOverrideReason, setDecisionOverrideReason] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [slotInterviewerEmail, setSlotInterviewerEmail] = useState('');
  const [slotInterviewTitle, setSlotInterviewTitle] = useState('Next interview round');
  const [slotDurationMinutes, setSlotDurationMinutes] = useState(60);
  const [slotPreInterviewNotes, setSlotPreInterviewNotes] = useState('');

  const EMAIL_MODAL_SUBJECTS = {
    rejection: 'Update on your application',
    next_round: 'Next steps — your application',
    offer: 'Update on your application — offer',
    hired: 'Congratulations — you are hired',
  };

  const EMAIL_MODAL_TITLES = {
    rejection: 'Send rejection email',
    next_round: 'Send next-round email',
    offer: 'Send offer email',
    hired: 'Send hire confirmation',
  };

  const openEmailModal = useCallback(
    (kind) => {
      if (!jobId || !resumeId) return;
      setRejectResult(null);
      setDecisionOverrideReason('');
      setSlotPreInterviewNotes('');
      setSlotInterviewTitle('Next interview round');
      setSlotDurationMinutes(60);
      setSlotInterviewerEmail('');
      setRecipientEmail(isUsableCandidateEmail(candidateEmail) ? String(candidateEmail).trim() : '');
      const u = getCachedUser();
      const fromJob = (jobCompanyName && String(jobCompanyName).trim()) || '';
      const fromCache =
        (u?.company?.name && String(u.company.name).trim()) ||
        (u?.company_name && String(u.company_name).trim()) ||
        '';
      setRejectCompanyName(fromJob || fromCache);
      setRejectSubject(EMAIL_MODAL_SUBJECTS[kind] || EMAIL_MODAL_SUBJECTS.rejection);
      setEmailModalKind(kind);
    },
    [jobId, resumeId, jobCompanyName, candidateEmail]
  );

  const closeEmailModal = useCallback(() => {
    setEmailModalKind(null);
    setRejectResult(null);
    setRejectCustomMessage('');
    setDecisionOverrideReason('');
    setSlotInterviewerEmail('');
    setSlotPreInterviewNotes('');
  }, []);

  const outcomeEvalForLock = useMemo(
    () => evaluation || savedEvaluation,
    [evaluation, savedEvaluation]
  );
  const emailActionsLocked = isCandidateOutcomeEmailSent(outcomeEvalForLock);

  const emailModalNeedsOverride =
    Boolean(emailModalKind) &&
    (emailModalKind === 'offer' || emailModalKind === 'next_round' || emailModalKind === 'hired') &&
    evaluationNeedsOverrideForPositiveAction(evaluation || savedEvaluation);

  const confirmEmailDisabled =
    rejectSending ||
    !isUsableCandidateEmail(recipientEmail) ||
    (emailModalKind === 'next_round' && !slotInterviewerEmail.trim()) ||
    (emailModalNeedsOverride && decisionOverrideReason.trim().length < 10);

  const buildOutcomeMergeExtra = useCallback(() => {
    const src = evaluation || savedEvaluation;
    const trimmed = decisionOverrideReason.trim();
    const needs = evaluationNeedsOverrideForPositiveAction(src);
    const extra = {};
    if (needs && trimmed.length >= 10) {
      extra.override_reason = trimmed;
      extra.recommendation_at_send = src?.recommendation ?? null;
      extra.overall_score_at_send =
        src?.overall_score !== undefined && src?.overall_score !== null
          ? Number(src.overall_score)
          : null;
    }
    return extra;
  }, [evaluation, savedEvaluation, decisionOverrideReason]);

  const handleSendNextRoundSlotInvite = useCallback(async () => {
    if (!jobId || !resumeId) return;
    setRejectSending(true);
    setRejectResult(null);
    const src = evaluation || savedEvaluation;
    const needsOverride = evaluationNeedsOverrideForPositiveAction(src);
    const trimmedOverride = decisionOverrideReason.trim();
    if (needsOverride && trimmedOverride.length < 10) {
      setRejectResult({ error: 'Please enter a decision override reason (at least 10 characters).' });
      setRejectSending(false);
      return;
    }
    const inv = slotInterviewerEmail.trim();
    if (!inv) {
      setRejectResult({ error: 'Interviewer email is required to send scheduling slots.' });
      setRejectSending(false);
      return;
    }
    const toEmail = recipientEmail.trim();
    if (!isUsableCandidateEmail(toEmail)) {
      setRejectResult({ error: 'Enter a valid candidate email address before sending.' });
      setRejectSending(false);
      return;
    }
    try {
      const response = await apiFetch(`/api/jobs/${jobId}/send-next-round-slot-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resume_id: resumeId,
          interviewer_email: inv,
          interview_title: slotInterviewTitle.trim() || 'Next interview round',
          interview_type: 'Video Call',
          duration_minutes: Number(slotDurationMinutes) || 60,
          pre_interview_notes: slotPreInterviewNotes.trim() || null,
          decision_override_reason: trimmedOverride || null,
          requires_decision_override: needsOverride,
          recommendation_at_send: src?.recommendation ?? null,
          overall_score_at_send:
            src?.overall_score !== undefined && src?.overall_score !== null
              ? Number(src.overall_score)
              : null,
          recipient_email: toEmail,
        }),
      });
      const inviteBody = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          getUserFacingErrorMessage(
            response.status,
            inviteBody,
            formatApiErrorBody(inviteBody) || `Server error ${response.status}`
          )
        );
      }
      const result = inviteBody;
      setRejectResult(result);
      if (result.success || result.sent > 0) {
        setOutcomeFeedback('Next-round scheduling email sent with time-slot link.');
        const extra = buildOutcomeMergeExtra();
        setEvaluation((prev) =>
          prev ? mergeCandidateEmailOutcome(prev, 'next_round', extra) : prev
        );
        setSavedEvaluation((prev) =>
          prev ? mergeCandidateEmailOutcome(prev, 'next_round', extra) : prev
        );
        if (onEvaluationSaved) {
          onEvaluationSaved();
        }
      }
    } catch (error) {
      setRejectResult({ error: error.message });
    } finally {
      setRejectSending(false);
    }
  }, [
    jobId,
    resumeId,
    evaluation,
    savedEvaluation,
    decisionOverrideReason,
    slotInterviewerEmail,
    slotInterviewTitle,
    slotDurationMinutes,
    slotPreInterviewNotes,
    recipientEmail,
    buildOutcomeMergeExtra,
    onEvaluationSaved,
  ]);

  const handleSendCandidateEmailFromEval = useCallback(async () => {
    if (!jobId || !resumeId || !emailModalKind) return;
    if (emailModalKind === 'next_round') {
      await handleSendNextRoundSlotInvite();
      return;
    }
    setRejectSending(true);
    setRejectResult(null);
    const src = evaluation || savedEvaluation;
    const needsOverride = evaluationNeedsOverrideForPositiveAction(src);
    const trimmedOverride = decisionOverrideReason.trim();
    if (
      (emailModalKind === 'offer' || emailModalKind === 'next_round' || emailModalKind === 'hired') &&
      needsOverride &&
      trimmedOverride.length < 10
    ) {
      setRejectResult({ error: 'Please enter a decision override reason (at least 10 characters).' });
      setRejectSending(false);
      return;
    }
    const toEmail = recipientEmail.trim();
    if (!isUsableCandidateEmail(toEmail)) {
      setRejectResult({ error: 'Enter a valid candidate email address before sending.' });
      setRejectSending(false);
      return;
    }
    try {
      const extraBody = {};
      if (needsOverride && trimmedOverride.length >= 10) {
        extraBody.decision_override_reason = trimmedOverride;
        extraBody.requires_decision_override = true;
        extraBody.recommendation_at_send = src?.recommendation ?? null;
        extraBody.overall_score_at_send =
          src?.overall_score !== undefined && src?.overall_score !== null
            ? Number(src.overall_score)
            : null;
      }
      const response = await apiFetch(`/api/jobs/${jobId}/send-candidate-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resume_ids: [resumeId],
          email_kind: emailModalKind,
          subject: rejectSubject,
          custom_message: rejectCustomMessage || null,
          company_name: rejectCompanyName || null,
          recipient_email: toEmail,
          ...extraBody,
        }),
      });
      const emailBody = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          getUserFacingErrorMessage(
            response.status,
            emailBody,
            formatApiErrorBody(emailBody) || `Server error ${response.status}`
          )
        );
      }
      const result = emailBody;
      setRejectResult(result);
      if (result.sent > 0) {
        const msg =
          emailModalKind === 'hired'
            ? 'Hire confirmation sent.'
            : emailModalKind === 'offer'
            ? 'Offer email sent.'
            : emailModalKind === 'next_round'
            ? 'Next-round email sent.'
            : 'Rejection email sent.';
        setOutcomeFeedback(msg);
        const mergeExtra = {};
        if (needsOverride && trimmedOverride.length >= 10) {
          mergeExtra.override_reason = trimmedOverride;
          mergeExtra.recommendation_at_send = src?.recommendation ?? null;
          mergeExtra.overall_score_at_send =
            src?.overall_score !== undefined && src?.overall_score !== null
              ? Number(src.overall_score)
              : null;
        }
        setEvaluation((prev) =>
          prev ? mergeCandidateEmailOutcome(prev, emailModalKind, mergeExtra) : prev
        );
        setSavedEvaluation((prev) =>
          prev ? mergeCandidateEmailOutcome(prev, emailModalKind, mergeExtra) : prev
        );
        if (onEvaluationSaved) {
          onEvaluationSaved();
        }
      }
    } catch (error) {
      setRejectResult({ error: error.message });
    } finally {
      setRejectSending(false);
    }
  }, [
    jobId,
    resumeId,
    emailModalKind,
    rejectSubject,
    rejectCustomMessage,
    rejectCompanyName,
    evaluation,
    savedEvaluation,
    decisionOverrideReason,
    recipientEmail,
    handleSendNextRoundSlotInvite,
    onEvaluationSaved,
  ]);

  // Ensure the modal behaves as a true full-viewport overlay (independent of parent layout).
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    // Only (re)initialize the "already evaluated" landing screen when the modal OPENS,
    // not on every re-render (otherwise clicking "View Results" gets reset immediately).
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (!isOpen) {
      staleQuestionnaireLatchRef.current = false;
      // Reset per-session flags on close
      setHasCheckedSavedResults(false);
      setBypassSavedEvaluation(false);
      setIsReevaluationMode(false);
      setShowChoiceMode(false);
      setUseTranscript(false);
      setTranscriptText('');
      setIsExtractingAnswers(false);
      setTranscriptTemporaryId(null);
      setIsTranscriptEvaluating(false);
      setTranscriptEvalMode('map_questionnaire');
      setExtractedQAPairs([]);
      setTranscriptMeta(null);
      setCandidateSpeakerOverride('');
      setInterviewerSpeakersOverride([]);
      setForceLLMExtraction(false);
      setShowCasualPreview(false);
      setTranscriptHighlight(false);
      setEmailModalKind(null);
      setRejectResult(null);
      setRejectCustomMessage('');
      setOutcomeFeedback(null);
      return;
    }
    if (!wasOpen && isOpen) {
      setHasCheckedSavedResults(false);
      setEvaluation(null);
      setError(null);
      setShowSavedResults(false);
      setUseTranscript(false);
      setTranscriptText('');
      setIsExtractingAnswers(false);
      setTranscriptTemporaryId(null);
      setIsTranscriptEvaluating(false);
      setTranscriptEvalMode('map_questionnaire');
      setExtractedQAPairs([]);
      setTranscriptMeta(null);
      setCandidateSpeakerOverride('');
      setInterviewerSpeakersOverride([]);
      setForceLLMExtraction(false);
      setShowCasualPreview(false);
      setTranscriptHighlight(false);

      // Determine if we should show unified main UI (choice mode)
      // Show unified UI if: no questionnaire OR no evaluation completed (or bypassed)
      // This ensures unified UI is shown when "Conduct Interview" is clicked
      // Persisted results: open straight to results view (Re-evaluate lives there). Flag-only completed → landing.
      const resultsPersisted = hasPersistedInterviewEvaluation(evaluationResults);
      const hasSaved = (resultsPersisted || evaluationCompleted) && !bypassSavedEvaluation;
      if (hasSaved && !bypassSavedEvaluation) {
        if (resultsPersisted) {
          try {
            const normalized =
              typeof evaluationResults === 'string' ? JSON.parse(evaluationResults) : evaluationResults;
            // Go straight to results — skip "Evaluation Complete" / View Results click.
            setSavedEvaluation(normalized);
            setEvaluation(normalized);
            setIsReevaluationMode(false);
            setShowChoiceMode(false);
            setShowSavedResults(true);
          } catch (e) {
            // If parsing fails, fall back to choice hub
            setSavedEvaluation(evaluationResults);
            setEvaluation(null);
            setIsReevaluationMode(false);
            setShowChoiceMode(true);
            setShowSavedResults(false);
          }
        } else {
          // No structured persisted evaluation — fall back to choice hub
          setSavedEvaluation(null);
          setShowSavedResults(false);
          setEvaluation(null);
          setIsReevaluationMode(false);
          setShowChoiceMode(true);
        }
        try {
          const normalizedPayload = typeof evaluationPayload === 'string' ? JSON.parse(evaluationPayload) : evaluationPayload;
          setSavedPayload(normalizedPayload || null);
          if (normalizedPayload?.transcript_eval_mode) {
            // With a questionnaire, default to map mode; extract_qa is for transcript-only flows.
            const m = normalizedPayload.transcript_eval_mode;
            if (questionnaire && m === 'extract_qa') {
              setTranscriptEvalMode('map_questionnaire');
            } else {
              setTranscriptEvalMode(m);
            }
          }
          if (Array.isArray(normalizedPayload?.extracted_qa)) {
            setExtractedQAPairs(normalizedPayload.extracted_qa);
          }
          // candidate_groups and evaluationByCandidate no longer used (single-candidate model)
        } catch (e) {
          setSavedPayload(evaluationPayload || null);
        }
      } else {
        // No saved evaluation - show unified UI if no questionnaire
        setSavedEvaluation(null);
        setShowSavedResults(false);
        setSavedPayload(null);
        if (!questionnaire) {
          setShowChoiceMode(true);
        } else {
          setShowChoiceMode(false);
        }
      }
      // Latch: opened straight to questions while server may still return eval fields on next paint/refetch
      staleQuestionnaireLatchRef.current = !!(questionnaire && !hasSaved);
    }

    // If modal is already open and there's no questionnaire, show choice mode again
    // — unless the user just left choice mode to generate questions (bypassSavedEvaluation) or generation started.
    // Without the bypass check, the first click on "Generate Questionnaire" set showChoiceMode false and this
    // block immediately set it true again, forcing a double-click.
    if (
      isOpen &&
      !questionnaire &&
      !showChoiceMode &&
      wasOpen &&
      !useTranscript &&
      !showSavedResults &&
      !evaluation &&
      !bypassSavedEvaluation &&
      !isGeneratingQuestionnaire
    ) {
      // Modal is already open, no questionnaire exists - show choice mode
      // This handles the case where user clicked back during generation and then clicks "Conduct Interview" again
      // BUT don't reset if user is actively using transcript extraction or started questionnaire generation
      console.log('🔙 [Modal] Modal already open with no questionnaire, showing choice mode');
      setShowChoiceMode(true);
    }
    
    // If modal is already open and user navigated back (showChoiceMode is true),
    // ensure choice mode is shown even if generation is in progress
    if (isOpen && showChoiceMode && isGeneratingQuestionnaire && !questionnaire) {
      // User clicked back during generation - keep showing choice mode
      // Generation can continue in background, but user can choose different option
      console.log('🔙 [Modal] User navigated back during generation, keeping choice mode visible');
    }
    
    if (isOpen && questionnaire) {
      
      const questions = [];
      let questionIndex = 1;

      // Process introduction questions FIRST
      if (questionnaire.introduction && Array.isArray(questionnaire.introduction)) {
        questionnaire.introduction.forEach((q, index) => {
          questions.push({
            id: `introduction_${index}`,
            section: 'introduction',
            sectionTitle: 'INTRODUCTION',
            question: q.question,
            expectedAnswer: q.expected_answer_summary || [],
            weightage: q.weightage || 10,
            index: questionIndex++
          });
        });
      }

      // Process behavioral questions
      if (questionnaire.behavioral && Array.isArray(questionnaire.behavioral)) {
        questionnaire.behavioral.forEach((q, index) => {
          questions.push({
            id: `behavioral_${index}`,
            section: 'behavioral',
            sectionTitle: 'BEHAVIORAL',
            question: q.question,
            expectedAnswer: q.expected_answer_summary || [],
            weightage: q.weightage || 7.5,
            index: questionIndex++
          });
        });
      }

      // Process situational questions
      if (questionnaire.situational && Array.isArray(questionnaire.situational)) {
        questionnaire.situational.forEach((q, index) => {
          questions.push({
            id: `situational_${index}`,
            section: 'situational',
            sectionTitle: 'SITUATIONAL',
            question: q.question,
            expectedAnswer: q.expected_answer_summary || [],
            weightage: q.weightage || 7.5,
            index: questionIndex++
          });
        });
      }

      // Process technical theoretical questions
      if (questionnaire.technical?.theoretical && Array.isArray(questionnaire.technical.theoretical)) {
        questionnaire.technical.theoretical.forEach((q, index) => {
          questions.push({
            id: `technical_theoretical_${index}`,
            section: 'technical_theoretical',
            sectionTitle: 'TECHNICAL THEORETICAL',
            question: q.question,
            expectedAnswer: q.expected_answer_summary || [],
            weightage: q.weightage || 6.67,
            index: questionIndex++
          });
        });
      }

      // Process technical practical questions
      if (questionnaire.technical?.practical && Array.isArray(questionnaire.technical.practical)) {
        questionnaire.technical.practical.forEach((q, index) => {
          questions.push({
            id: `technical_practical_${index}`,
            section: 'technical_practical',
            sectionTitle: 'TECHNICAL PRACTICAL',
            question: q.question,
            expectedAnswer: q.expected_answer_summary || [],
            weightage: q.weightage || 6.67,
            index: questionIndex++
          });
        });
      }

      console.log("🎯 Flattened questions:", questions);
      console.log("🎯 Flattened questions length:", questions.length);
      setFlattenedQuestions(questions);
      setIsProcessing(false);
      
      // Only notify parent that questionnaire is ready if we're not generating additional questions
      if (onQuestionnaireReady && !isGeneratingQuestionnaire) {
        onQuestionnaireReady();
      }

      // Initialize answers, scores, and notes
      // If in re-evaluation mode and savedPayload exists, load saved answers
      let initialAnswers = {};
      let initialScores = {};
      let initialNotes = {};
      
      // Load saved answers from savedPayload if in re-evaluation mode
      if (isReevaluationMode && savedPayload && savedPayload.answers) {
        initialAnswers = { ...savedPayload.answers };
        console.log('🔄 [Re-evaluate] Loading saved answers:', initialAnswers);
      } else {
        // Preserve existing answers if any
        initialAnswers = { ...answers };
      }
      
      // Load saved scores if available
      if (isReevaluationMode && savedPayload && savedPayload.scores) {
        initialScores = { ...savedPayload.scores };
      } else {
        initialScores = { ...scores };
      }
      
      // Load saved notes if available
      if (isReevaluationMode && savedPayload && savedPayload.notes) {
        initialNotes = { ...savedPayload.notes };
      } else {
        initialNotes = { ...notes };
      }

      // Initialize any missing question IDs with empty values
      questions.forEach(q => {
        // Only initialize if not already set (preserve loaded/saved values)
        if (!(q.id in initialAnswers)) {
          initialAnswers[q.id] = '';
        }
        if (!(q.id in initialScores)) {
          initialScores[q.id] = 0;
        }
        if (!(q.id in initialNotes)) {
          initialNotes[q.id] = '';
        }
      });

      setAnswers(initialAnswers);
      setScores(initialScores);
      setNotes(initialNotes);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    questionnaire,
    evaluationCompleted,
    evaluationResults,
    evaluationPayload,
    bypassSavedEvaluation,
    showSavedResults,
    isReevaluationMode,
    savedPayload,
    isGeneratingQuestionnaire,
  ]);

// If we opened on questions due to stale eval props, jump to saved results when props update.
useEffect(() => {
  if (!isOpen) return;
  if (
    !staleQuestionnaireLatchRef.current ||
    bypassSavedEvaluation ||
    isReevaluationMode ||
    showSavedResults ||
    evaluation ||
    useTranscript
  ) {
    return;
  }
  const nowPersisted = hasPersistedInterviewEvaluation(evaluationResults);
  const nowHasSaved = (nowPersisted || evaluationCompleted) && !bypassSavedEvaluation;
  if (!nowHasSaved || !questionnaire || showChoiceMode) return;

  staleQuestionnaireLatchRef.current = false;
  if (nowPersisted) {
    try {
      const normalized =
        typeof evaluationResults === 'string' ? JSON.parse(evaluationResults) : evaluationResults;
      setSavedEvaluation(normalized);
      setEvaluation(normalized);
      setShowSavedResults(true);
      setIsReevaluationMode(false);
      setShowChoiceMode(false);
    } catch (e) {
      setSavedEvaluation(evaluationResults);
      const ev =
        typeof evaluationResults === 'object' && evaluationResults !== null ? evaluationResults : null;
      setEvaluation(ev);
      setShowSavedResults(!!ev);
      setIsReevaluationMode(false);
      setShowChoiceMode(!ev);
    }
  } else {
    setSavedEvaluation(null);
    setShowSavedResults(false);
    setEvaluation(null);
    setIsReevaluationMode(false);
    setShowChoiceMode(true);
  }
  try {
    const normalizedPayload =
      typeof evaluationPayload === 'string' ? JSON.parse(evaluationPayload) : evaluationPayload;
    setSavedPayload(normalizedPayload || null);
    if (normalizedPayload?.transcript_eval_mode) {
      const m = normalizedPayload.transcript_eval_mode;
      if (questionnaire && m === 'extract_qa') {
        setTranscriptEvalMode('map_questionnaire');
      } else {
        setTranscriptEvalMode(m);
      }
    }
    if (Array.isArray(normalizedPayload?.extracted_qa)) {
      setExtractedQAPairs(normalizedPayload.extracted_qa);
    }
    // candidate_groups / evaluation_by_candidate: legacy payload fields (single-candidate UI)
  } catch (e) {
    setSavedPayload(evaluationPayload || null);
  }
}, [
  isOpen,
  questionnaire,
  evaluationResults,
  evaluationCompleted,
  evaluationPayload,
  bypassSavedEvaluation,
  isReevaluationMode,
  showSavedResults,
  evaluation,
  useTranscript,
  showChoiceMode,
]);

// Handler for generating questionnaire from choice mode
const handleGenerateQuestionnaire = async () => {
  console.log('📋 [Generate Questionnaire] Button clicked');
  staleQuestionnaireLatchRef.current = false;
    setShowChoiceMode(false);
    setEvaluation(null);
    setSavedEvaluation(null);
    setShowSavedResults(false);
    setBypassSavedEvaluation(true);
    setAnswers({});
    setScores({});
    setNotes({});
    setError(null);
    setUseTranscript(false);
    setTranscriptText('');
    setExtractedQAPairs([]);
    if (onGenerateQuestionnaire) {
      await onGenerateQuestionnaire();
    }
  };

  const fetchLatestSavedResults = useCallback(async () => {
    if (!jobId || !resumeId) return null;
    try {
      const res = await apiFetch(`/api/jobs/${jobId}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) return null;
      const data = await res.json().catch(() => ({}));
      const resumes = Array.isArray(data?.resumes) ? data.resumes : [];
      const latestResume = resumes.find((r) => Number(r?.id) === Number(resumeId));
      if (!latestResume) return null;

      const rawResults = latestResume.evaluation_results;
      const rawPayload = latestResume.evaluation_payload;
      const parsedResults = typeof rawResults === 'string' ? JSON.parse(rawResults) : rawResults;
      const parsedPayload = typeof rawPayload === 'string' ? JSON.parse(rawPayload) : rawPayload;

      if (!hasPersistedInterviewEvaluation(parsedResults)) return null;

      setSavedEvaluation(parsedResults);
      setSavedPayload(parsedPayload || null);
      return parsedResults;
    } catch (e) {
      console.warn('⚠️ [View Results] Failed to fetch latest saved results:', e);
      return null;
    }
  }, [jobId, resumeId]);

  useEffect(() => {
    if (
      !isOpen ||
      bypassSavedEvaluation ||
      !showChoiceMode ||
      !evaluationCompleted ||
      savedEvaluation ||
      isLoadingSavedResults ||
      hasCheckedSavedResults ||
      !jobId ||
      !resumeId
    ) {
      return;
    }

    let cancelled = false;

    const loadSavedResults = async () => {
      setIsLoadingSavedResults(true);
      const latest = await fetchLatestSavedResults();
      if (cancelled) return;
      setIsLoadingSavedResults(false);
      setHasCheckedSavedResults(true);

      if (latest) {
        setError(null);
      }
    };

    loadSavedResults();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    bypassSavedEvaluation,
    showChoiceMode,
    evaluationCompleted,
    savedEvaluation,
    isLoadingSavedResults,
    hasCheckedSavedResults,
    jobId,
    resumeId,
    fetchLatestSavedResults,
  ]);

  const handleExtractFromTranscript = () => {
    console.log('📝 [Extract Transcript] Button clicked');
    staleQuestionnaireLatchRef.current = false;
    setShowChoiceMode(false);
    setUseTranscript(true);
    setTranscriptEvalMode('extract_qa');
    setEvaluation(null);
    setSavedEvaluation(null);
    setShowSavedResults(false);
    setBypassSavedEvaluation(true);
    setAnswers({});
    setScores({});
    setNotes({});
    setError(null);
    setExtractedQAPairs([]);
    if (isGeneratingQuestionnaire && onCancelQuestionnaireGeneration) {
      onCancelQuestionnaireGeneration();
    }
    setTimeout(() => {
      if (transcriptTextareaRef.current) {
        transcriptTextareaRef.current.focus();
      }
    }, 100);
  };

  const handleBackToChoice = () => {
    if (isGeneratingQuestionnaire && onClose) {
      onClose();
      return;
    }
    const resultsPersisted = hasPersistedInterviewEvaluation(evaluationResults);
    if (resultsPersisted) {
      try {
        const normalized =
          typeof evaluationResults === 'string' ? JSON.parse(evaluationResults) : evaluationResults;
        setSavedEvaluation(normalized);
      } catch (e) {
        setSavedEvaluation(evaluationResults);
      }
      try {
        const normalizedPayload =
          typeof evaluationPayload === 'string' ? JSON.parse(evaluationPayload) : evaluationPayload;
        setSavedPayload(normalizedPayload || null);
      } catch (e) {
        setSavedPayload(evaluationPayload || null);
      }
      setBypassSavedEvaluation(false);
      setHasCheckedSavedResults(false);
    }
    setShowChoiceMode(true);
    setUseTranscript(false);
    setTranscriptText('');
    setTranscriptEvalMode('map_questionnaire');
    setExtractedQAPairs([]);
    setTranscriptMeta(null);
    setCandidateSpeakerOverride('');
    setInterviewerSpeakersOverride([]);
    setForceLLMExtraction(false);
    setShowCasualPreview(false);
    setError(null);
    setEvaluation(null);
    setShowSavedResults(false);
  };

  /** Extract Q&A from transcript only (direct transcript mode). Map-to-questionnaire uses handleExtractAnswersFromTranscript + bottom Evaluate. */
  const handleEvaluateWithAI = async () => {
    if (!useTranscript || !transcriptText.trim()) {
      setError('Please enable transcript mode and paste the transcript first.');
      focusTranscriptBox();
      return;
    }

    if (transcriptEvalMode === 'map_questionnaire') {
      setError('Use “Map to questionnaire” to fill answers, set scores, then click Evaluate below.');
      return;
    }

    setIsTranscriptEvaluating(true);
    setError(null);
    try {
      let latestEvaluation = null;
      let latestAnswers = answers;
      let latestExtractedQA = extractedQAPairs;

      const activePreviewQAPairs = getActiveTranscriptQAPairs();
      const canReusePreview =
        Array.isArray(activePreviewQAPairs) &&
        activePreviewQAPairs.length > 0 &&
        String(transcriptText || '').trim().length > 0;

      const res = await apiFetch('/api/transcripts/evaluate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          transcript_text: transcriptText,
          candidate_name: candidateName,
          fit_score: fitScore,
          job_title: jobTitle,
          use_llm: !forceLLMExtraction,
          candidate_speaker: candidateSpeakerOverride || undefined,
          interviewer_speakers: interviewerSpeakersOverride && interviewerSpeakersOverride.length > 0 ? interviewerSpeakersOverride : undefined,
          extracted_qa: canReusePreview ? activePreviewQAPairs : undefined,
          resume_id: resumeId,
          job_id: jobId
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(
          getUserFacingErrorMessage(
            res.status,
            data,
            data.message || data.error || 'Failed to evaluate from transcript Q&A'
          )
        );
      }
      latestExtractedQA = Array.isArray(data.extracted_qa) ? data.extracted_qa : [];
      latestEvaluation = data.evaluation;
      setExtractedQAPairs(latestExtractedQA);
      setEvaluation(data.evaluation);
      setTranscriptMeta(data.meta || {});
      setShowSavedResults(false);

      if (resumeId && latestEvaluation) {
        const evaluationPayload = {
          questionnaire,
          answers: latestAnswers,
          scores,
          notes,
          transcript_text: transcriptText,
          transcript_eval_mode: transcriptEvalMode,
          extracted_qa: latestExtractedQA,
        };
        const saveResponse = await apiFetch('/api/save-evaluation-results', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            resume_id: resumeId,
            evaluation_results: latestEvaluation,
            evaluation_payload: evaluationPayload
          })
        });
        const saveData = await saveResponse.json().catch(() => ({}));
        if (!saveResponse.ok || saveData?.success === false) {
          const saveErrorMessage =
            saveData?.detail ||
            saveData?.error ||
            saveData?.message ||
            'Evaluation generated, but saving failed. Please try again.';
          setError(saveErrorMessage);
        } else if (onEvaluationSaved) {
          onEvaluationSaved(latestEvaluation, evaluationPayload);
        }
      }
    } catch (e) {
      setError(e.message || 'Failed to evaluate with AI.');
    } finally {
      setIsTranscriptEvaluating(false);
    }
  };

  const focusTranscriptBox = () => {
    try {
      setUseTranscript(true);
      setTranscriptHighlight(true);
      setTimeout(() => setTranscriptHighlight(false), 1200);
      setTimeout(() => {
        if (transcriptTextareaRef.current) {
          transcriptTextareaRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          transcriptTextareaRef.current.focus();
        }
      }, 0);
    } catch (e) {}
  };

  const getActiveTranscriptQAPairs = () => {
    if (!Array.isArray(extractedQAPairs)) return [];
    return extractedQAPairs;
  };

  const visibleTranscriptQAPairs = (() => {
    const active = getActiveTranscriptQAPairs();
    return showCasualPreview
      ? active
      : active.filter((qa) => String(qa?.kind || '').toLowerCase() !== 'casual');
  })();

  const handleEvaluateFromTranscriptAI = async () => {
    if (!questionnaire) {
      setError('No questionnaire available. Please generate questions first.');
      return;
    }
    if (!useTranscript || !transcriptText.trim()) {
      setError('Please enable transcript mode and paste the transcript first.');
      focusTranscriptBox();
      return;
    }
    if (!flattenedQuestions || flattenedQuestions.length === 0) {
      setError('No questions available to evaluate.');
      return;
    }

    setIsTranscriptEvaluating(true);
    setError(null);
    try {
      const questions = flattenedQuestions.map((q) => ({
        id: q.id,
        section: q.section,
        sectionTitle: q.sectionTitle,
        question: q.question,
        expectedAnswer: q.expectedAnswer || []
      }));

      const response = await apiFetch('/api/evaluate-interview-transcript', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          questions,
          answers,
          scores,
          candidate_name: candidateName,
          fit_score: fitScore,
          job_title: jobTitle,
          transcript_text: transcriptText,
          temporary_interview_id: transcriptTemporaryId,
          resume_id: resumeId,
          job_id: jobId
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(
          getUserFacingErrorMessage(
            response.status,
            data,
            data.error || 'Failed to evaluate interview from transcript'
          )
        );
      }

      const evalObj = data.evaluation;
      setEvaluation(evalObj);
      setShowSavedResults(false);

      // Persist (same mechanism as manual evaluation)
      if (resumeId) {
        const payload = {
          questionnaire,
          answers,
          scores: evalObj?.question_scores_by_id || {},
          notes,
          transcript_text: transcriptText,
          evaluation_source: 'transcript_ai'
        };

        const saveResponse = await apiFetch('/api/save-evaluation-results', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            resume_id: resumeId,
            evaluation_results: evalObj,
            evaluation_payload: payload
          })
        });
        const saveData = await saveResponse.json().catch(() => ({}));
        if (!saveResponse.ok) {
          console.warn('Failed to save transcript AI evaluation results:', saveData);
        } else if (onEvaluationSaved) {
          onEvaluationSaved(evalObj, payload);
        }
      }
    } catch (e) {
      setError(e.message || 'Failed to evaluate from transcript.');
    } finally {
      setIsTranscriptEvaluating(false);
    }
  };

  const handleExtractAnswersFromTranscript = async () => {
    if (!transcriptText.trim()) {
      setError('Please paste the interview transcript first.');
      focusTranscriptBox();
      return;
    }
    if (!flattenedQuestions || flattenedQuestions.length === 0) {
      setError('No questions available to map transcript answers.');
      return;
    }

    setIsExtractingAnswers(true);
    setError(null);
    try {
      const questions = flattenedQuestions.map((q) => ({
        id: q.id,
        question: q.question
      }));

      const res = await apiFetch('/api/transcripts/extract-answers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          transcript_text: transcriptText,
          questions,
          temporary_interview_id: transcriptTemporaryId,
          resume_id: resumeId,
          job_id: jobId
        })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(
          getUserFacingErrorMessage(
            res.status,
            data,
            data.error || data.message || 'Failed to extract answers from transcript'
          )
        );
      }

      if (data.temporary_interview_id) {
        setTranscriptTemporaryId(data.temporary_interview_id);
      }

      const raw = data.extracted_answers || {};
      // Ensure keys match renderQuestion (always string ids like introduction_0)
      const extracted = Object.fromEntries(
        Object.entries(raw).map(([k, v]) => [String(k), typeof v === 'string' ? v : v == null ? '' : String(v)])
      );
      setAnswers((prev) => ({ ...prev, ...extracted }));
    } catch (e) {
      setError(e.message || 'Failed to extract answers from transcript.');
    } finally {
      setIsExtractingAnswers(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleScoreChange = (questionId, score) => {
    setScores(prev => ({
      ...prev,
      [questionId]: score
    }));
  };

  const handleNotesChange = (questionId, value) => {
    setNotes(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleEvaluate = async () => {
    if (!questionnaire) {
      setError('No questionnaire available. Please generate questions first.');
      return;
    }

    setIsEvaluating(true);
    setError(null);

    // DEBUG: Log all data being sent
    console.log("🔍 FRONTEND DEBUG: Starting evaluation");
    console.log("🔍 FRONTEND DEBUG: Questionnaire:", questionnaire);
    console.log("🔍 FRONTEND DEBUG: Answers:", answers);
    console.log("🔍 FRONTEND DEBUG: Scores:", scores);
    console.log("🔍 FRONTEND DEBUG: Candidate:", candidateName, "Fit:", fitScore, "Job:", jobTitle);
    
    // DEBUG: Log scores in detail
    console.log("�� FRONTEND DEBUG: Scores breakdown:");
    Object.entries(scores).forEach(([key, value]) => {
      console.log(`🔍 FRONTEND DEBUG: ${key}: ${value}`);
    });

    try {
      const useCombinedTranscriptEval =
        useTranscript &&
        transcriptEvalMode === 'map_questionnaire' &&
        String(transcriptText || '').trim().length > 0;

      let data;
      if (useCombinedTranscriptEval) {
        if (!flattenedQuestions || flattenedQuestions.length === 0) {
          throw new Error('No questionnaire questions to evaluate.');
        }
        const evalPayloadQuestions = flattenedQuestions.map((q) => ({
          id: q.id,
          section: q.section,
          sectionTitle: q.sectionTitle,
          question: q.question,
          expectedAnswer: q.expectedAnswer || []
        }));

        const response = await apiFetch('/api/evaluate-interview-transcript', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            questions: evalPayloadQuestions,
            answers,
            scores,
            candidate_name: candidateName,
            fit_score: fitScore,
            job_title: jobTitle,
            transcript_text: transcriptText,
            temporary_interview_id: transcriptTemporaryId,
            resume_id: resumeId,
            job_id: jobId
          })
        });

        const raw = await response.json().catch(() => ({}));
        if (!response.ok || raw.success === false) {
          throw new Error(
            getUserFacingErrorMessage(
              response.status,
              raw,
              formatApiErrorBody(raw) ||
                raw.error ||
                raw.message ||
                'Failed to evaluate interview'
            )
          );
        }
        data = raw;
      } else {
        const response = await apiFetch('/api/evaluate-interview', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            questionnaire: questionnaire,
            answers: answers,
            scores: scores,
            candidate_name: candidateName,
            fit_score: fitScore,
            job_title: jobTitle,
            resume_id: resumeId,
            job_id: jobId
          }),
        });

        const evalRaw = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            getUserFacingErrorMessage(
              response.status,
              evalRaw,
              formatApiErrorBody(evalRaw) || 'Failed to evaluate interview'
            )
          );
        }

        data = evalRaw;
      }

      setEvaluation(data.evaluation);
      setShowSavedResults(false);

      // Save evaluation results to backend
      try {
        if (!resumeId) {
          console.error('❌ No resumeId provided, cannot save evaluation results');
        } else {
          const evaluationPayload = {
            questionnaire,
            answers,
            scores,
            notes,
            ...(useCombinedTranscriptEval
              ? { transcript_text: transcriptText, transcript_eval_mode: 'map_questionnaire' }
              : {}),
          };
          const saveResponse = await apiFetch('/api/save-evaluation-results', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              resume_id: resumeId,
              evaluation_results: data.evaluation,
              evaluation_payload: evaluationPayload
            })
          });

          const saveRaw = await saveResponse.json().catch(() => ({}));
          if (!saveResponse.ok) {
            throw new Error(
              getUserFacingErrorMessage(
                saveResponse.status,
                saveRaw,
                formatApiErrorBody(saveRaw) || 'Failed to save evaluation results'
              )
            );
          }

          console.log('✅ Evaluation results saved successfully');
          setSavedPayload({ questionnaire, answers, scores, notes });

          if (onEvaluationSaved) {
            onEvaluationSaved();
          }
        }
      } catch (saveError) {
        console.error('Error saving evaluation results:', saveError);
      }
    } catch (error) {
      console.error('Error evaluating interview:', error);
      setError(error.message || 'Failed to evaluate interview. Please try again.');
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    setEvaluation(null);
  };

  // Removed unused function - getQuestionCategory

  const getCategoryIcon = (category) => {
    const icons = {
      'INTRODUCTION': '👋',
      'TECHNICAL THEORETICAL': '📚',
      'TECHNICAL PRACTICAL': '🔧',
      'BEHAVIORAL': '👥',
      'SITUATIONAL': '🎯'
    };
    return icons[category] || '📝';
  };

  const processStrengthsAndWeaknesses = (evaluation) => {
    if (!evaluation.category_averages) return { strengths: [], weaknesses: [] };
    
    const strengths = [];
    const weaknesses = [];
    
    Object.entries(evaluation.category_averages).forEach(([category, average]) => {
      if (average >= 1.5) {
        strengths.push(`${getCategoryIcon(category)} ${category}: Strong performance`);
      } else {
        weaknesses.push(`${getCategoryIcon(category)} ${category}: Needs improvement`);
      }
    });
    
    return { strengths, weaknesses };
  };

  const handleTestAPI = async () => {
    try {
      // Test the actual evaluate-interview endpoint with dummy data
      const testData = {
        questionnaire: questionnaire,
        answers: { 'test': 'This is a test response' },
        candidate_name: candidateName,
        fit_score: fitScore,
        job_title: jobTitle
      };
      
      const response = await apiFetch('/api/evaluate-interview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(testData)
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('API test result:', data);
        alert('API test successful! Check console for details.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('API test failed:', errorData);
        alert(`API test failed: ${formatApiErrorBody(errorData) || errorData.error || response.status}`);
      }
    } catch (err) {
      console.error('API test failed:', err);
      alert('API test failed. Check console for details.');
    }
  };

  const renderQuestion = (question, index) => {
    // Use question text as unique identifier to preserve user input across updates
    const questionId = question.id || `question_${question.question.substring(0, 50).replace(/[^a-zA-Z0-9]/g, '_')}`;
    const currentScore = scores[questionId] || 0;

    return (
      <div key={questionId} className="question-evaluation-item">
        <div className="question-header">
          <div className="question-type-section">
            <span className="question-type" data-section={question.section}>
              {question.sectionTitle}
            </span>
          </div>
          <span className="question-number">#{question.index}</span>
        </div>

        <div className="question-text">{question.question}</div>
        
        {question.expectedAnswer && question.expectedAnswer.length > 0 && (
          <div className="expected-answer-summary-section">
            <div className="expected-answer-summary-label">
              <span>📋</span>
              <span>Expected Answer Summary</span>
            </div>
            <div className="expected-answer-summary-list">
              {question.expectedAnswer.map((point, pointIndex) => (
                <div key={pointIndex} className="expected-answer-point">
                  <span className="point-bullet">•</span>
                  <span className="point-text">{point}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="answer-input-section">
          <label className="answer-label">Candidate Response:</label>
          <textarea
            value={answers[questionId] || ''}
            onChange={(e) => handleAnswerChange(questionId, e.target.value)}
            placeholder="Enter the candidate's response here..."
            className="answer-input"
            rows={4}
          />
          <div className="character-count">
            {(answers[questionId] || '').length} characters
          </div>
        </div>

        <div className="notes-section">
          <span className="notes-icon">📝</span>
          <span className="notes-label">Hiring Manager's Notes</span>
          <textarea
            value={notes[questionId] || ''}
            onChange={(e) => handleNotesChange(questionId, e.target.value)}
            placeholder="Add your evaluation notes here..."
            className="notes-input"
            rows={3}
          />
        </div>
        
        <div className="question-scoring">
          <span className="scoring-label">Score:</span>
          <div className="scoring-buttons">
            <button 
              className={`scoring-button ${currentScore === 0 ? 'active' : ''}`}
              onClick={() => handleScoreChange(questionId, 0)}
              style={{background: currentScore === 0 ? '#dc2626' : '#ffffff1a'}}
            >
              0
            </button>
            <button 
              className={`scoring-button ${currentScore === 1 ? 'active' : ''}`}
              onClick={() => handleScoreChange(questionId, 1)}
              style={{background: currentScore === 1 ? '#ea580c' : '#ffffff1a'}}
            >
              1
            </button>
            <button 
              className={`scoring-button ${currentScore === 2 ? 'active' : ''}`}
              onClick={() => handleScoreChange(questionId, 2)}
              style={{background: currentScore === 2 ? '#16a34a' : '#ffffff1a'}}
            >
              2
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderTranscriptQAPreviewQuestion = (qa, index) => {
    const qid = qa?.qid || `tq_${index + 1}`;
    const sectionTitle = qa?.sectionTitle || 'TRANSCRIPT Q&A';
    const section = qa?.section || 'transcript_qa';
    const normalizeSectionKey = (s) => {
      const raw = String(s || '').trim();
      if (!raw) return 'transcript_qa';
      // Backend may send TitleCase keys; CSS expects lowercase keys.
      if (raw === 'Introduction') return 'introduction';
      if (raw === 'Behavioral') return 'behavioral';
      if (raw === 'Situational') return 'situational';
      if (raw === 'Technical_practical') return 'technical_practical';
      if (raw === 'Technical_theoretical') return 'technical_theoretical';
      return raw.toLowerCase();
    };
    const badgeSectionKey = normalizeSectionKey(section);
    const qText = qa?.question_text || '—';
    const aText = qa?.answer_text || 'No clear answer found in transcript.';

    return (
      <div key={`tqa_${qid}`} className="question-evaluation-item">
        <div className="question-header">
          <div className="question-type-section">
            <span className="question-type" data-section={badgeSectionKey}>
              {sectionTitle}
            </span>
          </div>
          <span className="question-number">#{index + 1}</span>
        </div>

        <div className="question-text">{qText}</div>

        <div className="answer-input-section">
          <label className="answer-label">Candidate Response (from transcript):</label>
          <textarea
            value={aText}
            disabled
            className="answer-input"
            rows={4}
          />
          <div className="character-count">
            {aText.length} characters
          </div>
        </div>
      </div>
    );
  };

  const renderResponsesReadOnly = () => {
    const payload = savedPayload;
    const payloadQuestionnaire = payload?.questionnaire || null;
    const ans = payload?.answers || {};
    const sc = payload?.scores || {};
    const nt = payload?.notes || {};

    if (!payload || Object.keys(ans).length === 0) {
      return (
        <div className="loading-section">
          <h3>🗒️ No saved responses found</h3>
          <p>This evaluation was saved before responses tracking was enabled.</p>
        </div>
      );
    }

    // Use the questionnaire snapshot used during evaluation (if present),
    // otherwise fall back to the currently loaded questionnaire/questions.
    const questionsSource = payloadQuestionnaire || questionnaire;
    const questions = [];
    let questionIndex = 1;

    const pushSection = (sectionId, sectionTitle, arr) => {
      if (!Array.isArray(arr)) return;
      arr.forEach((q, idx) => {
        if (!q) return;
        questions.push({
          id: `${sectionId}_${idx}`,
          section: sectionId,
          sectionTitle,
          question: q.question || String(q),
          expectedAnswer: q.expected_answer_summary || [],
          weightage: q.weightage || 0,
          index: questionIndex++
        });
      });
    };

    if (questionsSource) {
      pushSection('introduction', 'INTRODUCTION', questionsSource.introduction);
      pushSection('behavioral', 'BEHAVIORAL', questionsSource.behavioral);
      pushSection('situational', 'SITUATIONAL', questionsSource.situational);
      pushSection('technical_theoretical', 'TECHNICAL THEORETICAL', questionsSource.technical?.theoretical);
      pushSection('technical_practical', 'TECHNICAL PRACTICAL', questionsSource.technical?.practical);
    }

    const renderList = questions.length > 0 ? questions : flattenedQuestions;

    const getQuestionId = (question) => {
      // Keep consistent with renderQuestion() ID derivation
      return (
        question.id ||
        `question_${(question.question || '')
          .substring(0, 50)
          .replace(/[^a-zA-Z0-9]/g, '_')}`
      );
    };

    return (
      <div className="questions-section">
        <div className="questions-header">
          <h4>📝 Candidate Responses</h4>
          <p>Read-only view of the latest saved questionnaire responses.</p>
        </div>

        <div className="questions-list">
          {renderList.map((q) => {
            const qid = getQuestionId(q);
            const currentScore = typeof sc[qid] === 'number' ? sc[qid] : 0;
            return (
              <div key={`ro_${qid}`} className="question-evaluation-item">
                <div className="question-header">
                  <div className="question-type-section">
                    <span className="question-type" data-section={q.section}>
                      {q.sectionTitle}
                    </span>
                  </div>
                  <span className="question-number">#{q.index}</span>
                </div>

                <div className="question-text">{q.question}</div>

                {q.expectedAnswer && q.expectedAnswer.length > 0 && (
                  <div className="expected-answer-summary-section">
                    <div className="expected-answer-summary-label">
                      <span>📋</span>
                      <span>Expected Answer Summary</span>
                    </div>
                    <div className="expected-answer-summary-list">
                      {q.expectedAnswer.map((point, pointIndex) => (
                        <div key={pointIndex} className="expected-answer-point">
                          <span className="point-bullet">•</span>
                          <span className="point-text">{point}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="answer-input-section">
                  <label className="answer-label">Candidate Response:</label>
                  <textarea
                    value={ans[qid] || ''}
                    readOnly
                    disabled
                    className="answer-input"
                    rows={4}
                  />
                  <div className="character-count">
                    {(ans[qid] || '').length} characters
                  </div>
                </div>

                <div className="notes-section">
                  <span className="notes-icon">📝</span>
                  <span className="notes-label">Hiring Manager's Notes</span>
                  <textarea
                    value={nt[qid] || ''}
                    readOnly
                    disabled
                    className="notes-input"
                    rows={3}
                  />
                </div>

                <div className="question-scoring">
                  <span className="scoring-label">Score:</span>
                  <div className="scoring-buttons">
                    <button
                      type="button"
                      disabled
                      className={`scoring-button ${currentScore === 0 ? 'active' : ''}`}
                      style={{ background: currentScore === 0 ? '#dc2626' : '#ffffff1a' }}
                    >
                      0
                    </button>
                    <button
                      type="button"
                      disabled
                      className={`scoring-button ${currentScore === 1 ? 'active' : ''}`}
                      style={{ background: currentScore === 1 ? '#ea580c' : '#ffffff1a' }}
                    >
                      1
                    </button>
                    <button
                      type="button"
                      disabled
                      className={`scoring-button ${currentScore === 2 ? 'active' : ''}`}
                      style={{ background: currentScore === 2 ? '#16a34a' : '#ffffff1a' }}
                    >
                      2
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderEvaluation = () => {
    if (!evaluation) return null;

    const renderPerQuestionBreakdown = () => {
      if (!evaluation?.question_scores_by_id) return null;

      const scoresById = evaluation.question_scores_by_id || {};
      const rationalesById = evaluation.question_rationales_by_id || {};
      const evidenceById = evaluation.question_evidence_by_id || {};

      const isQA = evaluation?.evaluation_source === 'transcript_qa_ai';
      const sourceQuestions = isQA
        ? (Array.isArray(extractedQAPairs) ? extractedQAPairs.map((qa, i) => {
            const id = qa.qid || `tq_${i + 1}`;
            const sectionTitle =
              evaluation?.question_section_title_by_id?.[id] ||
              (evaluation?.question_section_by_id?.[id] ? String(evaluation.question_section_by_id[id]).toUpperCase() : 'TRANSCRIPT Q&A');
            return { id, index: i + 1, sectionTitle };
          }) : [])
        : flattenedQuestions;

      if (!sourceQuestions || sourceQuestions.length === 0) return null;

      return (
        <div className="per-question-breakdown">
          <h5>🧾 Per-question score breakdown</h5>
          <div className="pq-table-wrap">
            <table className="pq-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Section</th>
                  <th>Score</th>
                  <th>Reason</th>
                  <th>Evidence</th>
                </tr>
              </thead>
              <tbody>
                {sourceQuestions.map((q) => {
                  const qid = q.id;
                  const score = typeof scoresById[qid] === 'number' ? scoresById[qid] : null;
                  const reason = rationalesById?.[qid] || '';
                  const evidence = evidenceById?.[qid] || '';
                  return (
                    <tr key={`pq_${qid}`}>
                      <td className="pq-num">#{q.index}</td>
                      <td className="pq-sec">{q.sectionTitle}</td>
                      <td className={`pq-score ${score === 2 ? 's2' : score === 1 ? 's1' : 's0'}`}>
                        {score === null ? '—' : `${score}/2`}
                      </td>
                      <td className="pq-reason">{reason || '—'}</td>
                      <td className="pq-evidence">{evidence || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    };

    return (
      <div className="evaluation-results">
        <div className="evaluation-header">
          <h4>Interview Evaluation Results</h4>
          <div className="candidate-info">
            <span className="candidate-name">{candidateName}</span>
            <span className="fit-score">Fit Score: {fitScore}</span>
          </div>
        </div>

        <div className="overall-score-section">
          <div className="score-display">
            <span className="score-label">Overall Score</span>
            <span className="score-value">{evaluation.overall_score || 0}/100</span>
          </div>
          <div className="recommendation-badge">
            <span className={`recommendation ${(evaluation.recommendation || '').toLowerCase().replace(' ', '-')}`}>
              {evaluation.recommendation || 'Pending'}
            </span>
          </div>
        </div>

        {evaluation.candidate_email_outcome?.kind && (
          <div className="interview-eval-email-outcome">
            <span
              className={`interview-eval-email-outcome-badge interview-eval-email-outcome-${evaluation.candidate_email_outcome.kind}`}
            >
              {emailOutcomeLabel(evaluation.candidate_email_outcome)}
            </span>
            {evaluation.candidate_email_outcome.sent_at && (
              <span className="interview-eval-email-outcome-meta">
                {new Date(evaluation.candidate_email_outcome.sent_at).toLocaleString()}
              </span>
            )}
            {evaluation.candidate_email_outcome.override_reason && (
              <div className="interview-eval-override-reason" style={{ marginTop: '0.75rem' }}>
                <strong>Decision override reason:</strong>
                <p style={{ margin: '0.35rem 0 0', whiteSpace: 'pre-wrap' }}>
                  {evaluation.candidate_email_outcome.override_reason}
                </p>
              </div>
            )}
          </div>
        )}

        {evaluation.category_averages && Object.keys(evaluation.category_averages).length > 0 && (
          <div className="section-scores">
            <h5>📊 Category Scores</h5>
            <div className="scores-grid">
              {(() => {
                const order = ['Introduction', 'Behavioral', 'Situational', 'Technical_practical', 'Technical_theoretical', 'transcript_qa'];
                const entries = Object.entries(evaluation.category_averages);
                entries.sort((a, b) => {
                  const ai = order.indexOf(a[0]);
                  const bi = order.indexOf(b[0]);
                  return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                });
                return entries;
              })().map(([category, average]) => (
                <div key={category} className="score-item">
                  <span className="score-category">{category}</span>
                  <span className="score-value">{average}/2</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(evaluation?.evaluation_source === 'transcript_ai' ||
          evaluation?.evaluation_source === 'transcript_qa_ai' ||
          evaluation?.evaluation_source === 'interview_evaluation') &&
          renderPerQuestionBreakdown()}

        {(() => {
          const { strengths, weaknesses } = processStrengthsAndWeaknesses(evaluation);
          return (
            <>
              {strengths.length > 0 && (
                <div className="strengths-section">
                  <h5>✅ Strengths</h5>
                  <ul>
                    {strengths.map((strength, index) => (
                      <li key={index}>{strength}</li>
                    ))}
                  </ul>
                </div>
              )}

              {weaknesses.length > 0 && (
                <div className="improvements-section">
                  <h5>🔧 Areas for Improvement</h5>
                  <ul>
                    {weaknesses.map((weakness, index) => (
                      <li key={index}>{weakness}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          );
        })()}

        {evaluation.final_assessment && (
          <div className="final-assessment">
            <h5>📝 Final Assessment</h5>
            <p>{evaluation.final_assessment}</p>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return createPortal(
    <>
    <div className="interview-evaluation-modal-overlay">
      <div className="interview-evaluation-modal">
        <div className="interview-evaluation-modal-header">
          <div className="header-content">
            <h2>Interview Evaluation</h2>
            <p className="header-subtitle">
              Evaluating {candidateName} for {jobTitle}
            </p>
          </div>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="interview-evaluation-modal-content">
          {/* Unified Main UI: Shows when showChoiceMode is true */}
          {showChoiceMode && (
            <div className="interview-choice-mode">
              <div className="choice-mode-container">
                {/* Show "Interview Already Evaluated" when we have saved results or server says completed */}
                {(savedEvaluation || evaluationCompleted) && !showSavedResults && !evaluation && (
                  <>
                    <div className="evaluation-complete-section">
                      <div className="evaluation-complete-card">
                        <div className="evaluation-complete-header">
                          <span className="evaluation-complete-icon">✅</span>
                          <h3 className="evaluation-complete-title">Evaluation Complete</h3>
                        </div>
                        <p className="evaluation-complete-description">
                          This candidate has been evaluated. You can view the results.
                        </p>
                        {evaluationCompleted && !savedEvaluation && hasCheckedSavedResults && !isLoadingSavedResults && (
                          <p className="evaluation-complete-description" style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#c9a227' }}>
                            Results are still loading. Close this dialog and click <strong>Conduct Interview</strong> again, or refresh the page.
                          </p>
                        )}
                        <div className="evaluation-complete-actions">
                          <button
                            className="evaluation-action-button evaluation-action-primary"
                            onClick={async () => {
                              setBypassSavedEvaluation(false);
                              setError(null);
                              let resultToShow = savedEvaluation;

                              if (!resultToShow) {
                                setIsLoadingSavedResults(true);
                                resultToShow = await fetchLatestSavedResults();
                                setIsLoadingSavedResults(false);
                              }

                              if (resultToShow) {
                                setEvaluation(resultToShow);
                                setShowSavedResults(true);
                                setIsReevaluationMode(false);
                                setShowChoiceMode(false);
                              } else {
                                setError('Results are still being saved. Please try again in a few seconds.');
                              }
                            }}
                            disabled={isLoadingSavedResults}
                            style={isLoadingSavedResults ? { opacity: 0.7, cursor: 'wait' } : undefined}
                          >
                            <span className="evaluation-action-icon">📊</span>
                            <span>{isLoadingSavedResults ? 'View Results (loading...)' : 'View Results'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Start New Evaluation Section - hidden when prior evaluation exists */}
                {!((savedEvaluation || evaluationCompleted) && !showSavedResults && !evaluation) && (
                <div className="new-evaluation-section">
                  <div className="new-evaluation-card">
                    <div className="new-evaluation-header">
                      <span className="new-evaluation-icon">🚀</span>
                      <h3 className="new-evaluation-title">
                        {(savedEvaluation || evaluationCompleted) && !showSavedResults && !evaluation
                          ? "Start New Evaluation"
                          : "How would you like to proceed?"}
                      </h3>
                    </div>
                    <p className="new-evaluation-description">
                      {(savedEvaluation || evaluationCompleted) && !showSavedResults && !evaluation
                        ? "Choose how you'd like to proceed with a fresh evaluation:"
                        : "Choose an option to start the interview evaluation. You can generate a questionnaire or extract Q&A directly from a transcript."}
                    </p>
                    <div className="new-evaluation-buttons">
                      <button
                        type="button"
                        onClick={handleGenerateQuestionnaire}
                        disabled={isGeneratingQuestionnaire && !showChoiceMode}
                        className="new-evaluation-button new-evaluation-button-questionnaire"
                      >
                        {/* Only show "Generating..." if generation is actually in progress AND we're not in choice mode */}
                        {isGeneratingQuestionnaire && !showChoiceMode ? (
                          <>
                            <span className="new-evaluation-button-icon">⏳</span>
                            <span className="new-evaluation-button-text">Generating...</span>
                          </>
                        ) : (
                          <>
                            <span className="new-evaluation-button-icon">📋</span>
                            <span className="new-evaluation-button-text">Generate Questionnaire</span>
                          </>
                        )}
                      </button>
                      
                      <button
                        type="button"
                        onClick={handleExtractFromTranscript}
                        className="new-evaluation-button new-evaluation-button-transcript"
                      >
                        <span className="new-evaluation-button-icon">📝</span>
                        <span className="new-evaluation-button-text">Extract from Transcript</span>
                      </button>
                    </div>
                  </div>
                </div>
                )}
                
                {error && (
                  <div className="choice-mode-error">
                    {error}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Only show loading screen if generating AND not in choice mode AND no questionnaire yet */}
          {isGeneratingQuestionnaire && !showChoiceMode && !questionnaire && (
            <div className="loading-section">
              <div className="loading-spinner" aria-hidden="true"></div>
              <h3>Generating Additional Interview Questions</h3>
              <p>Please wait while we generate personalized interview questions for this candidate...</p>
              <button
                onClick={handleBackToChoice}
                className="back-to-choice-button"
                style={{ marginTop: '1.5rem' }}
                title="Go back to choose evaluation method"
              >
                <span className="back-button-icon">←</span>
                <span>Back to Options</span>
              </button>
            </div>
          )}
          
          {error && !showChoiceMode && (
            (() => {
              const lower = String(error || '').toLowerCase();
              const isTranscriptGuidance =
                lower.includes('transcript') ||
                lower.includes('paste the interview transcript') ||
                lower.includes('enable transcript mode');

              return (
                <div className={isTranscriptGuidance ? 'info-section' : 'error-section'}>
                  <h3>{isTranscriptGuidance ? 'ℹ️ Transcript needed' : '❌ Error'}</h3>
                  <p>{error}</p>
                  <div className="error-actions">
                    {isTranscriptGuidance ? (
                      <>
                        <button
                          className="retry-button"
                          onClick={() => {
                            setError(null);
                            focusTranscriptBox();
                          }}
                        >
                          📝 Paste transcript
                        </button>
                        <button className="test-api-button" onClick={() => setError(null)}>
                          ✅ Got it
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="retry-button" onClick={handleRetry}>
                          🔄 Try Again
                        </button>
                        <button className="test-api-button" onClick={handleTestAPI}>
                          🔧 Test API
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })()
          )}

          {/* Show evaluation results only when viewing results (not questionnaire) */}
          {savedEvaluation && showSavedResults && evaluation && !showChoiceMode && (
            <>
              {/* Show ONLY evaluation results, not the questionnaire */}
              <div className="results-section">
                {outcomeFeedback && (
                  <div className="interview-eval-outcome-banner" role="status">
                    {outcomeFeedback}
                  </div>
                )}
                {renderEvaluation()}
                {emailActionsLocked && (
                  <p className="interview-eval-actions-locked-notice" role="status">
                    A candidate email was already sent for this evaluation. Re-evaluate and the three email actions
                    are disabled. You can still use <strong>Close</strong>.
                  </p>
                )}
                <div className="results-actions interview-eval-outcome-actions">
                  <button
                    className="action-button secondary"
                    disabled={emailActionsLocked}
                    aria-disabled={emailActionsLocked}
                    title={emailActionsLocked ? 'A candidate email was already sent for this evaluation.' : undefined}
                    onClick={() => {
                      setBypassSavedEvaluation(true);
                      setShowSavedResults(false);
                      setEvaluation(null);
                      setError(null);
                      setIsReevaluationMode(true);
                      setShowChoiceMode(false);
                    }}
                  >
                    🔄 Re-evaluate
                  </button>
                  {jobId && resumeId && (
                    <>
                      <button
                        type="button"
                        className="action-button secondary"
                        disabled={emailActionsLocked}
                        aria-disabled={emailActionsLocked}
                        title={emailActionsLocked ? 'A candidate email was already sent for this evaluation.' : undefined}
                        onClick={() => openEmailModal('next_round')}
                      >
                        📬 Next round — email
                      </button>
                      <button
                        type="button"
                        className="action-button secondary"
                        disabled={emailActionsLocked}
                        aria-disabled={emailActionsLocked}
                        title={emailActionsLocked ? 'A candidate email was already sent for this evaluation.' : undefined}
                        onClick={() => openEmailModal('offer')}
                      >
                        🎉 Offer — email
                      </button>
                      <button
                        type="button"
                        className="action-button secondary"
                        disabled={emailActionsLocked}
                        aria-disabled={emailActionsLocked}
                        title={emailActionsLocked ? 'A candidate email was already sent for this evaluation.' : undefined}
                        onClick={() => openEmailModal('hired')}
                      >
                        ✅ Hired — email
                      </button>
                      <button
                        type="button"
                        className="action-button secondary"
                        disabled={emailActionsLocked}
                        aria-disabled={emailActionsLocked}
                        title={emailActionsLocked ? 'A candidate email was already sent for this evaluation.' : undefined}
                        onClick={() => openEmailModal('rejection')}
                      >
                        📧 Rejection — email
                      </button>
                    </>
                  )}
                  <button type="button" className="action-button secondary" onClick={onClose}>
                    Close
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Show questionnaire/evaluation page when not in choice mode */}
          {/* Skip when already showing saved results (prevents duplicate results + questionnaire stack) */}
          {/* Allow showing questionnaire even if savedEvaluation exists when in re-evaluation mode */}
          {!showChoiceMode && !(savedEvaluation && showSavedResults && evaluation) && (
            // If extracting from transcript, show transcript UI even without questionnaire
            // BUT if evaluation exists, show results instead
            (useTranscript && transcriptEvalMode === 'extract_qa' && !questionnaire && !evaluation) ? (
              <div className="evaluation-content">
                <div className="questions-section">
                  <div className="questions-header">
                    <div className="questions-header-top">
                      <button
                        type="button"
                        onClick={handleBackToChoice}
                        className="back-to-choice-button"
                        title="Go back to choose evaluation method"
                      >
                        <span className="back-button-icon">←</span>
                        <span>Back</span>
                      </button>
                      <div className="questions-header-title-group">
                        <h4>📝 Extract Q&A from Transcript</h4>
                        <p>Paste the interview transcript below to extract questions and answers directly.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Transcript UI for extract mode (no questionnaire needed) */}
                {useTranscript && !showChoiceMode && (
                  <>
                    {/* Show info that we're in extract mode */}
                    {!questionnaire && transcriptEvalMode === 'extract_qa' && (
                      <div className="transcript-mode-card" style={{
                        background: 'rgba(102, 126, 234, 0.1)',
                        border: '1px solid rgba(102, 126, 234, 0.3)',
                        borderRadius: '12px',
                        padding: '1rem',
                        marginBottom: '1rem'
                      }}>
                        <div style={{
                          color: '#667eea',
                          fontWeight: '600',
                          marginBottom: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <span>📝</span>
                          Extract Q&A from Transcript Mode
                        </div>
                        <div style={{
                          color: '#aaa',
                          fontSize: '0.9rem'
                        }}>
                          Detects the interviewer's actual questions and the candidate's answers from the transcript. Works best when speaker labels (Interviewer/Candidate) are present.
                        </div>
                      </div>
                    )}

                    <div style={{ marginTop: '0.75rem' }}>
                      <textarea
                        className={`answer-input ${transcriptHighlight ? 'transcript-highlight' : ''}`}
                        rows={8}
                        value={transcriptText}
                        onChange={(e) => setTranscriptText(e.target.value)}
                        placeholder="Paste the interview transcript here (Interviewer/Candidate lines are okay)…"
                        ref={transcriptTextareaRef}
                      />
                      {!transcriptText.trim() && (
                        <div className="transcript-helper">
                          Paste the transcript to enable <b>🤖 Evaluate with AI</b>.
                        </div>
                      )}

                      <div className="transcript-actions">
                        <button
                          type="button"
                          className="action-button primary"
                          onClick={handleEvaluateWithAI}
                          disabled={
                            isTranscriptEvaluating ||
                            !transcriptText.trim() ||
                            (transcriptEvalMode === 'map_questionnaire' && !questionnaire)
                          }
                        >
                          {isTranscriptEvaluating ? 'Evaluating…' : '🤖 Evaluate with AI'}
                        </button>
                      </div>

                      <div className="character-count">{transcriptText.length} characters</div>
                    </div>
                  </>
                )}

                {/* Show extracted Q&A or empty state */}
                {transcriptEvalMode === 'extract_qa' && extractedQAPairs?.length > 0 ? (
                  <div className="extracted-qa-section">
                    <div className="extracted-qa-header">
                      <h5>🧾 Extracted Q&amp;A (generated from transcript)</h5>
                      <p>Preview of detected questions/answers in the same questionnaire layout.</p>
                      {transcriptMeta?.topology && (
                        <p>
                          Interview type: <b>{String(transcriptMeta.topology).replace('_', ' ')}</b>
                          {transcriptMeta?.topology_confidence !== undefined ? (
                            <> · Confidence: <b>{Math.round((transcriptMeta.topology_confidence || 0) * 100)}%</b></>
                          ) : null}
                        </p>
                      )}
                    </div>
                    <div className="questions-list">
                      {visibleTranscriptQAPairs.map((qa, idx) => renderTranscriptQAPreviewQuestion(qa, idx))}
                    </div>
                  </div>
                ) : transcriptEvalMode === 'extract_qa' ? (
                  <div className="extracted-qa-empty">
                    Paste a transcript and click <b>🤖 Evaluate with AI</b> to see the detected questions and answers here.
                  </div>
                ) : null}
              </div>
                ) : !questionnaire || showChoiceMode ? (
              // Don't show questionnaire if we're in choice mode
              // BUT if evaluation exists and NOT in re-evaluation mode, show results
              // If generating questionnaire, show loading
              isGeneratingQuestionnaire && !questionnaire ? (
                <div className="loading-section">
                  <div className="loading-spinner" aria-hidden="true"></div>
                  <h3>Generating Interview Questions</h3>
                  <p>Please wait while we generate personalized interview questions for this candidate...</p>
                  <button
                    onClick={handleBackToChoice}
                    className="back-to-choice-button"
                    style={{ marginTop: '1.5rem' }}
                    title="Go back to choose evaluation method"
                  >
                    <span className="back-button-icon">←</span>
                    <span>Back to Options</span>
                  </button>
                </div>
              ) : evaluation && !isReevaluationMode && !showSavedResults ? (
                // Only show results here if NOT viewing saved results (saved results are shown above)
                <div className="results-section">
                  {outcomeFeedback && (
                    <div className="interview-eval-outcome-banner" role="status">
                      {outcomeFeedback}
                    </div>
                  )}
                  {renderEvaluation()}
                  {emailActionsLocked && (
                    <p className="interview-eval-actions-locked-notice" role="status">
                      A candidate email was already sent for this evaluation. Re-evaluate and the three email actions
                      are disabled. You can still use <strong>Close</strong>.
                    </p>
                  )}
                  <div className="results-actions interview-eval-outcome-actions">
                    <button
                      className="action-button secondary"
                      disabled={emailActionsLocked}
                      aria-disabled={emailActionsLocked}
                      title={emailActionsLocked ? 'A candidate email was already sent for this evaluation.' : undefined}
                      onClick={() => {
                        setBypassSavedEvaluation(true);
                        setShowSavedResults(false);
                        setEvaluation(null);
                        setError(null);
                        setIsReevaluationMode(true);
                        setShowChoiceMode(false);
                      }}
                    >
                      🔄 Re-evaluate
                    </button>
                    {jobId && resumeId && (
                      <>
                        <button
                          type="button"
                          className="action-button secondary"
                          disabled={emailActionsLocked}
                          aria-disabled={emailActionsLocked}
                          title={emailActionsLocked ? 'A candidate email was already sent for this evaluation.' : undefined}
                          onClick={() => openEmailModal('next_round')}
                        >
                          📬 Next round — email
                        </button>
                        <button
                          type="button"
                          className="action-button secondary"
                          disabled={emailActionsLocked}
                          aria-disabled={emailActionsLocked}
                          title={emailActionsLocked ? 'A candidate email was already sent for this evaluation.' : undefined}
                          onClick={() => openEmailModal('offer')}
                        >
                          🎉 Offer — email
                        </button>
                        <button
                          type="button"
                          className="action-button secondary"
                          disabled={emailActionsLocked}
                          aria-disabled={emailActionsLocked}
                          title={emailActionsLocked ? 'A candidate email was already sent for this evaluation.' : undefined}
                          onClick={() => openEmailModal('hired')}
                        >
                          ✅ Hired — email
                        </button>
                        <button
                          type="button"
                          className="action-button secondary"
                          disabled={emailActionsLocked}
                          aria-disabled={emailActionsLocked}
                          title={emailActionsLocked ? 'A candidate email was already sent for this evaluation.' : undefined}
                          onClick={() => openEmailModal('rejection')}
                        >
                          📧 Rejection — email
                        </button>
                      </>
                    )}
                    <button type="button" className="action-button secondary" onClick={onClose}>
                      Close
                    </button>
                  </div>
                </div>
              ) : null
            ) : (
              <div className="evaluation-content">
                {!evaluation ? (
                  <div className="questions-section">
                    <div className="questions-header">
                      <div className="questions-header-top">
                        <button
                          type="button"
                          onClick={handleBackToChoice}
                          className="back-to-choice-button"
                          title="Go back to choose evaluation method"
                        >
                          <span className="back-button-icon">←</span>
                          <span>Back</span>
                        </button>
                        <div className="questions-header-title-group">
                          <h4>📝 Candidate Responses</h4>
                          <p>Enter the candidate's responses to each question and provide your evaluation:</p>
                        </div>
                      </div>
                      {isGeneratingQuestionnaire && (
                        <div className="background-generation-indicator">
                          <div className="generation-spinner"></div>
                          <span>Generating additional questions in the background...</span>
                        </div>
                      )}
                    </div>

                    {/* Transcript helper - Show as button when questionnaire exists */}
                    {questionnaire && !showChoiceMode && (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={() => {
                              setUseTranscript(!useTranscript);
                              if (!useTranscript) {
                                setTranscriptEvalMode('map_questionnaire');
                              }
                            }}
                            style={{
                              padding: '0.75rem 1.5rem',
                              fontSize: '0.95rem',
                              background: useTranscript 
                                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                : 'rgba(102, 126, 234, 0.1)',
                              color: useTranscript ? 'white' : '#667eea',
                              border: `2px solid ${useTranscript ? '#667eea' : '#667eea'}`,
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              transition: 'all 0.3s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                            onMouseEnter={(e) => {
                              if (!useTranscript) {
                                e.target.style.background = 'rgba(102, 126, 234, 0.2)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!useTranscript) {
                                e.target.style.background = 'rgba(102, 126, 234, 0.1)';
                              }
                            }}
                          >
                            <span>{useTranscript ? '✓' : '📝'}</span>
                            {useTranscript ? 'Transcript Mode Active' : 'Use Transcript'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Transcript UI - Show when useTranscript is true (either from choice mode or questionnaire mode) */}
                    {useTranscript && !showChoiceMode && (
                        <>
                          {/* Mode selection for questionnaire-backed transcript evaluation */}
                          {questionnaire && (
                            <div className="transcript-mode-card">
                              <div className="transcript-mode-title">Transcript evaluation mode</div>

                              <label className="transcript-mode-option">
                                <input
                                  type="radio"
                                  name="transcriptEvalMode"
                                  value="map_questionnaire"
                                  checked={transcriptEvalMode === 'map_questionnaire'}
                                  onChange={() => {
                                    setTranscriptEvalMode('map_questionnaire');
                                    setExtractedQAPairs([]);
                                  }}
                                />
                                <div>
                                  <div className="transcript-mode-label">Map to existing questionnaire</div>
                                  <div className="transcript-mode-help">
                                    Uses the saved questionnaire and fills answers from the transcript, then evaluates each question.
                                  </div>
                                </div>
                              </label>

                              <label className="transcript-mode-option">
                                <input
                                  type="radio"
                                  name="transcriptEvalMode"
                                  value="extract_qa"
                                  checked={transcriptEvalMode === 'extract_qa'}
                                  onChange={() => {
                                    setTranscriptEvalMode('extract_qa');
                                  }}
                                />
                                <div>
                                  <div className="transcript-mode-label">Evaluate transcript directly</div>
                                  <div className="transcript-mode-help">
                                    Extracts Q&A from the transcript and evaluates those extracted questions and answers.
                                  </div>
                                </div>
                              </label>
                            </div>
                          )}

                          {/* If no questionnaire, show info that we're in extract mode */}
                          {!questionnaire && transcriptEvalMode === 'extract_qa' && (
                            <div className="transcript-mode-card" style={{
                              background: 'rgba(102, 126, 234, 0.1)',
                              border: '1px solid rgba(102, 126, 234, 0.3)',
                              borderRadius: '12px',
                              padding: '1rem',
                              marginBottom: '1rem'
                            }}>
                              <div style={{
                                color: '#667eea',
                                fontWeight: '600',
                                marginBottom: '0.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}>
                                <span>📝</span>
                                Extract Q&A from Transcript Mode
                              </div>
                              <div style={{
                                color: '#aaa',
                                fontSize: '0.9rem'
                              }}>
                                Detects the interviewer's actual questions and the candidate's answers from the transcript. Works best when speaker labels (Interviewer/Candidate) are present.
                              </div>
                            </div>
                          )}

                          <div style={{ marginTop: '0.75rem' }}>
                            <textarea
                              className={`answer-input ${transcriptHighlight ? 'transcript-highlight' : ''}`}
                              rows={8}
                              value={transcriptText}
                              onChange={(e) => setTranscriptText(e.target.value)}
                              placeholder="Paste the interview transcript here (Interviewer/Candidate lines are okay)…"
                              ref={transcriptTextareaRef}
                            />
                            {!transcriptText.trim() && (
                              <div className="transcript-helper">
                                {transcriptEvalMode === 'map_questionnaire' ? (
                                  <>Paste the transcript, then click <b>Map</b>, set scores, and use <b>Evaluate</b> below.</>
                                ) : (
                                  <>Paste the transcript to enable <b>🤖 Evaluate with AI</b>.</>
                                )}
                              </div>
                            )}

                            <div className="transcript-actions" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                              {questionnaire && transcriptEvalMode === 'map_questionnaire' && (
                                <button
                                  type="button"
                                  className="action-button primary"
                                  onClick={handleExtractAnswersFromTranscript}
                                  disabled={
                                    isExtractingAnswers ||
                                    !transcriptText.trim() ||
                                    !questionnaire
                                  }
                                >
                                  {isExtractingAnswers ? 'Mapping…' : 'Map'}
                                </button>
                              )}
                              {transcriptEvalMode === 'extract_qa' && (
                                <button
                                  type="button"
                                  className="action-button primary"
                                  onClick={handleEvaluateWithAI}
                                  disabled={isTranscriptEvaluating || !transcriptText.trim()}
                                >
                                  {isTranscriptEvaluating ? 'Evaluating…' : '🤖 Evaluate with AI'}
                                </button>
                              )}
                            </div>

                            <div className="character-count">{transcriptText.length} characters</div>
                          </div>
                        </>
                    )}

                    {isProcessing ? (
                      <div className="loading-section">
                        <div className="loading-spinner" aria-hidden="true"></div>
                        <h3>Processing Interview Questions</h3>
                        <p>Please wait while we process your responses...</p>
                      </div>
                    ) : (
                      <>
                        {transcriptEvalMode === 'extract_qa' && extractedQAPairs?.length > 0 ? (
                          <div className="extracted-qa-section">
                            <div className="extracted-qa-header">
                              <h5>🧾 Extracted Q&amp;A (generated from transcript)</h5>
                              <p>Preview of detected questions/answers in the same questionnaire layout.</p>
                            </div>
                            <div className="questions-list">
                              {visibleTranscriptQAPairs.map((qa, idx) => renderTranscriptQAPreviewQuestion(qa, idx))}
                            </div>
                          </div>
                        ) : transcriptEvalMode === 'extract_qa' ? (
                          <div className="extracted-qa-empty">
                            Paste a transcript and click <b>🤖 Evaluate with AI</b> to see the detected questions and answers here.
                          </div>
                        ) : (
                          <div className="questions-list">
                            {console.log("🎯 Rendering questions:", flattenedQuestions)}
                            {flattenedQuestions.map((question, index) =>
                              renderQuestion(question, index)
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {transcriptEvalMode !== 'extract_qa' && (
                      <div className="evaluation-actions">
                        <button
                          className="evaluate-button"
                          onClick={handleEvaluate}
                          disabled={isEvaluating}
                        >
                          {isEvaluating ? '🔄 Evaluating...' : (isReevaluationMode ? '🔁 Run Re-evaluation' : 'Evaluate')}
                        </button>
                      </div>
                    )}
                  </div>
                ) : !showSavedResults ? (
                  <div className="results-section">
                    {outcomeFeedback && (
                      <div className="interview-eval-outcome-banner" role="status">
                        {outcomeFeedback}
                      </div>
                    )}
                    {renderEvaluation()}

                    {emailActionsLocked && (
                      <p className="interview-eval-actions-locked-notice" role="status">
                        A candidate email was already sent for this evaluation. Re-evaluate and the three email actions
                        are disabled. You can still use <strong>Close</strong>.
                      </p>
                    )}
                    <div className="results-actions interview-eval-outcome-actions">
                      <button
                        className="action-button secondary"
                        disabled={emailActionsLocked}
                        aria-disabled={emailActionsLocked}
                        title={emailActionsLocked ? 'A candidate email was already sent for this evaluation.' : undefined}
                        onClick={() => {
                          setEvaluation(null);
                          setIsReevaluationMode(true);
                          setShowChoiceMode(true); // Go back to unified main UI
                          setShowSavedResults(false);
                        }}
                      >
                        🔄 Re-evaluate
                      </button>
                      {jobId && resumeId && (
                        <>
                          <button
                            type="button"
                            className="action-button secondary"
                            disabled={emailActionsLocked}
                            aria-disabled={emailActionsLocked}
                            title={emailActionsLocked ? 'A candidate email was already sent for this evaluation.' : undefined}
                            onClick={() => openEmailModal('next_round')}
                          >
                            📬 Next round — email
                          </button>
                          <button
                            type="button"
                            className="action-button secondary"
                            disabled={emailActionsLocked}
                            aria-disabled={emailActionsLocked}
                            title={emailActionsLocked ? 'A candidate email was already sent for this evaluation.' : undefined}
                            onClick={() => openEmailModal('offer')}
                          >
                            🎉 Offer — email
                          </button>
                          <button
                            type="button"
                            className="action-button secondary"
                            disabled={emailActionsLocked}
                            aria-disabled={emailActionsLocked}
                            title={emailActionsLocked ? 'A candidate email was already sent for this evaluation.' : undefined}
                            onClick={() => openEmailModal('hired')}
                          >
                            ✅ Hired — email
                          </button>
                          <button
                            type="button"
                            className="action-button secondary"
                            disabled={emailActionsLocked}
                            aria-disabled={emailActionsLocked}
                            title={emailActionsLocked ? 'A candidate email was already sent for this evaluation.' : undefined}
                            onClick={() => openEmailModal('rejection')}
                          >
                            📧 Rejection — email
                          </button>
                        </>
                      )}
                      <button type="button" className="action-button secondary" onClick={onClose}>
                        Close
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            )
          )}
        </div>
      </div>
    </div>

    {emailModalKind && (
      <div
        className="reject-modal-overlay interview-eval-reject-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) closeEmailModal();
        }}
      >
        <div className="reject-modal">
          <div className="reject-modal-header">
            <h2>{EMAIL_MODAL_TITLES[emailModalKind] || 'Send email'}</h2>
            <button type="button" className="reject-modal-close" onClick={closeEmailModal} aria-label="Close">
              &times;
            </button>
          </div>

          {!rejectResult ? (
            <div className="reject-modal-body">
              <div className="reject-candidate-preview">
                <table className="reject-preview-table">
                  <thead>
                    <tr>
                      <th>Candidate</th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className={!isUsableCandidateEmail(recipientEmail) ? 'no-email-row' : ''}>
                      <td>{candidateName || 'Unknown'}</td>
                      <td>
                        {isUsableCandidateEmail(recipientEmail) ? (
                          recipientEmail.trim()
                        ) : (
                          <span className="no-email-label">No email on file</span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="reject-form-group" style={{ marginTop: '12px' }}>
                  <label htmlFor="interview-eval-recipient-email">Candidate email</label>
                  <input
                    id="interview-eval-recipient-email"
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    className="reject-input"
                    placeholder="candidate@example.com"
                  />
                  <span className="reject-field-hint">
                    {isUsableCandidateEmail(candidateEmail)
                      ? 'Pre-filled from the resume. Edit if needed.'
                      : 'No usable email on the resume — enter the candidate address to send.'}
                  </span>
                </div>
                {!isUsableCandidateEmail(recipientEmail) && (
                  <p className="reject-warning">
                    Enter a valid candidate email before sending.
                  </p>
                )}
              </div>

              {emailModalKind === 'next_round' ? (
                <>
                  <div className="reject-form-group">
                    <label htmlFor="interview-eval-slot-interviewer">Interviewer email (for calendar availability)</label>
                    <input
                      id="interview-eval-slot-interviewer"
                      type="email"
                      value={slotInterviewerEmail}
                      onChange={(e) => setSlotInterviewerEmail(e.target.value)}
                      className="reject-input"
                      placeholder="interviewer@company.com"
                    />
                    <span className="reject-field-hint">
                      The candidate will receive a link to pick a time slot (same flow as Schedule Interview).
                    </span>
                  </div>
                  <div className="reject-form-group">
                    <label htmlFor="interview-eval-slot-title">Interview title</label>
                    <input
                      id="interview-eval-slot-title"
                      type="text"
                      value={slotInterviewTitle}
                      onChange={(e) => setSlotInterviewTitle(e.target.value)}
                      className="reject-input"
                    />
                  </div>
                  <div className="reject-form-group">
                    <label htmlFor="interview-eval-slot-duration">Duration (minutes)</label>
                    <input
                      id="interview-eval-slot-duration"
                      type="number"
                      min={15}
                      max={240}
                      value={slotDurationMinutes}
                      onChange={(e) => setSlotDurationMinutes(parseInt(e.target.value, 10) || 60)}
                      className="reject-input"
                    />
                  </div>
                  <div className="reject-form-group">
                    <label htmlFor="interview-eval-slot-notes">Pre-interview notes (optional)</label>
                    <textarea
                      id="interview-eval-slot-notes"
                      value={slotPreInterviewNotes}
                      onChange={(e) => setSlotPreInterviewNotes(e.target.value)}
                      className="reject-textarea"
                      rows={2}
                      placeholder="Notes for the candidate or interviewer..."
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="reject-form-group">
                    <label htmlFor="interview-eval-reject-company">Company Name</label>
                    <input
                      id="interview-eval-reject-company"
                      type="text"
                      value={rejectCompanyName}
                      onChange={(e) => setRejectCompanyName(e.target.value)}
                      className="reject-input"
                      placeholder="Your company name (shown in the email)"
                    />
                    <span className="reject-field-hint">Pre-filled from your account. Edit if needed.</span>
                  </div>
                  <div className="reject-form-group">
                    <label htmlFor="interview-eval-reject-subject">Subject</label>
                    <input
                      id="interview-eval-reject-subject"
                      type="text"
                      value={rejectSubject}
                      onChange={(e) => setRejectSubject(e.target.value)}
                      className="reject-input"
                    />
                  </div>
                  <div className="reject-form-group">
                    <label htmlFor="interview-eval-reject-msg">Custom message (optional)</label>
                    <textarea
                      id="interview-eval-reject-msg"
                      value={rejectCustomMessage}
                      onChange={(e) => setRejectCustomMessage(e.target.value)}
                      className="reject-textarea"
                      placeholder="Add a personalised note to include in the email..."
                      rows={3}
                    />
                  </div>
                </>
              )}

              {emailModalNeedsOverride && (
                <div className="reject-form-group">
                  <label htmlFor="interview-eval-override-reason">
                    Reason for overriding the evaluation recommendation (required, min 10 characters)
                  </label>
                  <textarea
                    id="interview-eval-override-reason"
                    value={decisionOverrideReason}
                    onChange={(e) => setDecisionOverrideReason(e.target.value)}
                    className="reject-textarea"
                    placeholder="Explain why you are proceeding despite the evaluation outcome (e.g. strong panel signal, role change)..."
                    rows={4}
                  />
                </div>
              )}

              <div className="reject-modal-actions">
                <button type="button" className="btn-cancel" onClick={closeEmailModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-confirm-reject"
                  onClick={handleSendCandidateEmailFromEval}
                  disabled={confirmEmailDisabled}
                >
                  {rejectSending ? 'Sending...' : 'Confirm & Send'}
                </button>
              </div>
            </div>
          ) : rejectResult.error ? (
            <div className="reject-modal-body">
              <div className="reject-result-error">
                <p>
                  Failed to send{' '}
                  {emailModalKind === 'hired'
                    ? 'hire confirmation'
                    : emailModalKind === 'offer'
                    ? 'offer email'
                    : emailModalKind === 'next_round'
                    ? 'next-round email'
                    : 'rejection email'}
                  :
                </p>
                <p className="reject-error-msg">{rejectResult.error}</p>
              </div>
              <div className="reject-modal-actions">
                <button type="button" className="btn-cancel" onClick={closeEmailModal}>
                  Close
                </button>
                <button
                  type="button"
                  className="btn-confirm-reject"
                  onClick={() => {
                    setRejectResult(null);
                  }}
                >
                  Back to form
                </button>
              </div>
            </div>
          ) : (
            <div className="reject-modal-body">
              <div className="reject-result-summary">
                <h4>Results</h4>
                <div className="reject-result-stats">
                  {rejectResult.sent > 0 && <span className="stat-sent">{rejectResult.sent} sent</span>}
                  {rejectResult.failed > 0 && <span className="stat-failed">{rejectResult.failed} failed</span>}
                  {rejectResult.no_email > 0 && (
                    <span className="stat-noemail">{rejectResult.no_email} no email</span>
                  )}
                </div>
              </div>
              {rejectResult.details && (
                <table className="reject-preview-table reject-result-table">
                  <thead>
                    <tr>
                      <th>Candidate</th>
                      <th>Email</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rejectResult.details.map((d, i) => (
                      <tr key={i} className={`status-${d.status}`}>
                        <td>{d.candidate_name}</td>
                        <td>{d.email || '-'}</td>
                        <td>
                          <span className={`status-badge status-${d.status}`}>
                            {d.status === 'sent' ? 'Sent' : d.status === 'failed' ? 'Failed' : 'No Email'}
                          </span>
                          {d.error && <span className="status-error-detail"> - {d.error}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="reject-modal-actions">
                <button type="button" className="btn-confirm-reject" onClick={closeEmailModal}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )}
    </>
  , document.body);
};

export default InterviewEvaluationModal;
